import { toaster } from 'baseui/toast';
import {
  all,
  call,
  CallEffect,
  delay,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeEvery
} from 'redux-saga/effects';
import {
  findLockBySource,
  FindLockBySourceResponse
} from '../../../modules/find-locked-offer-by-source';
import { synchWithMerchandisingCache } from '../../../modules/offer-details/local-storage';
import { fetchRateQuote, FetchRateQuoteResult } from '../../../modules/rate-quote';
import {
  MerchandisingPayload,
  queueMerchandisingSave,
  saveMerchandising
} from '../../../modules/rate-quote/merchandising';
import { StandardizedResponse } from '../../../modules/standardize';
import { merchandisingPayloadSelector } from '../../../selectors/offer';
import { rateQuoteIdSelector } from '../../../selectors/rate-quote/rate-quote';
import { rateQuoteTitleSelector } from '../../../selectors/rate-quote/rate-quote-title';
import { getAllQuotesFilterOptionsBegin } from '../../all-quotes-filter-options/slice';
import { fetchDeals } from '../../deals/slice';
import {
  clearMerchandisingFromCache,
  FETCH_LOCKED_OFFER_BY_SOURCE_ACTION_TYPE,
  FETCH_RATE_QUOTE_ACTION_TYPE,
  FETCH_RATE_QUOTE_FAILURE_ACTION_TYPE,
  FETCH_RATE_QUOTE_SUCCESS_ACTION_TYPE,
  FetchLockedOfferBySourceAction,
  fetchLockedOfferBySourceFailure,
  fetchLockedOfferBySourceSuccess,
  fetchRateQuoteError,
  FetchRateQuoteFailureAction,
  FetchRateQuoteRequestAction,
  fetchRateQuoteSuccess,
  SAVE_MERCHANDISING_ACTION_TYPE,
  SAVE_MERCHANDISING_ERROR_ACTION_TYPE,
  SaveMerchandisingAction,
  saveMerchandisingError,
  SaveMerchandisingErrorAction,
  saveMerchandisingSuccess,
  synchedMerchandisingFromCache
} from '../slice';
const MAX_RETRIES = 49;
const INITIAL_DELAY = 500;
const MAX_DELAY = 20000;

export function* handleFetchRequest({
  payload: { rateQuoteId }
}: FetchRateQuoteRequestAction): Generator<CallEffect | PutEffect> {
  for (let i = 1; i <= MAX_RETRIES; i++) {
    try {
      const res = yield call(fetchRateQuote, rateQuoteId);
      const { success, error, json, code } = res as FetchRateQuoteResult;
      if (success) {
        // Check if there's cached data that wants to be restored.
        const cacheRes = yield call(synchWithMerchandisingCache, json!);
        const [rateQuote, mergeFromCache] = cacheRes as Iterable<any>;
        // Trigger action if merged from cache.
        if (mergeFromCache) {
          yield put(synchedMerchandisingFromCache());
        } else {
          yield put(clearMerchandisingFromCache(rateQuote.rateQuoteId));
        }
        yield put(fetchRateQuoteSuccess(rateQuote));
        yield put(fetchDeals(rateQuote.rateQuoteRequest.sourceId));
        if (rateQuote.status === 'Loading') {
          if (i === MAX_RETRIES) {
            yield put(
              fetchRateQuoteError({
                message: 'Error retrieving automated rates. Please re-load the page and try again',
                code: 504 // timeout
              })
            );
            break;
          }
          yield delay(Math.min(INITIAL_DELAY * Math.pow(2, i), MAX_DELAY));
        } else {
          yield put(
            getAllQuotesFilterOptionsBegin({
              rateQuoteRequestId: rateQuote.rateQuoteRequestId
            })
          );
          break;
        }
      } else {
        yield put(fetchRateQuoteError({ message: error!, code }));
        // retrying the same failed request won't work.
        if (code >= 400 && code < 500) {
          break;
        }
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : JSON.stringify(err);
      yield put(fetchRateQuoteError({ message: error, code: 500 }));
    }
  }
}

export function* handleFetchRequestSuccess(): Generator<SelectEffect> {
  const title = yield select(rateQuoteTitleSelector);

  // Update the document title.
  (window.document.title as unknown) = title;
}

function* handleFetchRequestFailure({ payload: { message, code } }: FetchRateQuoteFailureAction) {
  // authentication errors will trigger a login
  if (code !== 401) {
    yield call(() =>
      toaster.negative(`Error fetching rate quote. Try reloading the page. ${message}`, {
        closeable: true
      })
    );
  }
}

function* handleSaveMerchandising({ payload: saveImmediately }: SaveMerchandisingAction) {
  if (saveImmediately) {
    const rateQuoteId: string = yield select(rateQuoteIdSelector);
    const merchandisingSet: MerchandisingPayload = yield select(merchandisingPayloadSelector);
    const { success, error, json } = yield call(saveMerchandising, rateQuoteId, merchandisingSet);
    if (success) {
      yield put(saveMerchandisingSuccess(json!));
    } else {
      yield put(saveMerchandisingError(error!));
    }
  } else {
    yield call(queueMerchandisingSave);
  }
}

function* handleSaveMerchandisingError({ payload }: SaveMerchandisingErrorAction) {
  yield call(() =>
    toaster.negative(`Error saving merchandised offers. Try reloading the page. ${payload}`, {
      autoHideDuration: 5000
    })
  );
}

function* handleFetchLockedOfferBySource({
  payload: { sourceId, history }
}: FetchLockedOfferBySourceAction): Generator<CallEffect | PutEffect> {
  try {
    const lockedOffer = yield call(findLockBySource, sourceId);
    const { success, json, error, code } =
      lockedOffer as StandardizedResponse<FindLockBySourceResponse>;
    if (success && json) {
      if (json.hasLock && json.rateQuoteId) {
        yield put(
          fetchLockedOfferBySourceSuccess({
            status: 'completed',
            rateQuoteId: json.rateQuoteId
          })
        );
        history.push(`${json.rateQuoteId}`);
        return;
      }
      if (!json.hasLock) {
        yield put(
          fetchLockedOfferBySourceFailure({
            status: 'failed',
            error:
              'The current RateQuote has a locked offer that does not exist in any RateQuote on this source'
          })
        );
        return;
      }
    }
    if (code) {
      yield put(
        fetchLockedOfferBySourceFailure({
          status: 'failed',
          error,
          code
        })
      );
      return;
    }
  } catch (err) {
    const error = err instanceof Error ? err.message : JSON.stringify(err);
    yield put(
      fetchLockedOfferBySourceFailure({
        status: 'failed',
        error
      })
    );
  }
}

export const rateQuotesSaga = function* () {
  yield all([
    takeEvery(FETCH_RATE_QUOTE_ACTION_TYPE, handleFetchRequest),
    takeEvery(FETCH_RATE_QUOTE_SUCCESS_ACTION_TYPE, handleFetchRequestSuccess),
    takeEvery(FETCH_RATE_QUOTE_FAILURE_ACTION_TYPE, handleFetchRequestFailure),
    takeEvery(SAVE_MERCHANDISING_ACTION_TYPE, handleSaveMerchandising),
    takeEvery(SAVE_MERCHANDISING_ERROR_ACTION_TYPE, handleSaveMerchandisingError),
    takeEvery(FETCH_LOCKED_OFFER_BY_SOURCE_ACTION_TYPE, handleFetchLockedOfferBySource)
  ]);
};
